import { useCallback } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { LogLevel, useSeqLog } from "@/hooks/telemetry";
import type { ContainerPreset } from "@/constants/componentPresets";
import { CBUTTON_SURFACE_SELECTOR } from "../Cbutton";
import type { TeamData } from "../Cteamcard";
import type { MorphSource } from "../Cteamsgallery";
import {
  HERO_TEAM_CARD_BACKGROUND,
  HERO_TEAM_CARD_CONTENT_BACKDROP_BLUR,
} from "./constants";
import {
  type HeroCardSurfaceGeometry,
  resolveHeroCardMorphGeometry,
} from "./heroCardMorphSource";
import type { CarouselTeamSelectInput } from "./types";

const CLICK_INVESTIGATION_ID = "click-investigation--team-stack-card--2026-05-25";
const CAROUSEL_CARD_CLICK_INV_COMPONENT = "Cteamscarousel:CarouselCard";
const CAROUSEL_CARD_HANDLE_CLICK_EVENT = "click-inv:carousel-card-handleClick";

enum CarouselCardClickBranch {
  Select = "select",
  ScrollToIndex = "scrollToIndex",
}

interface UseCarouselCardClickHandlerParams {
  cardContainerPreset?: ContainerPreset;
  doubledIndex: number;
  isCentered: boolean;
  onScrollToIndex: (doubledIndex: number) => void;
  onSelectTeam: (input: CarouselTeamSelectInput) => void;
  team: TeamData;
}

/**
 * Reads the clicked hero card's VISUAL surface rect + radius.
 *
 * Cbutton stamps {@link CBUTTON_SURFACE_SELECTOR} on the one element that carries
 * the card's real corner radius + background (its surface `Ccontainer` root); the
 * surrounding hit wrapper and the inner content wrapper are both square (radius 0).
 *
 * `event.currentTarget` is not fixed to one node: depending on where Cbutton binds
 * the click it can be the surface itself, a content descendant of it, or the outer
 * hit wrapper. Its own radius is therefore unreliable. We resolve to the marked
 * surface in all three positions — `closest` covers self/descendant, `querySelector`
 * covers the wrapper (surface is a child). The marker is a production attribute
 * owned by Cbutton, so the seam is stable in every build. Returns `null` (→ radius
 * fallback) when no surface or readable radius exists — a genuine square surface
 * keeps its 0.
 */
function readHeroCardSurfaceGeometry(
  clicked: HTMLElement,
): HeroCardSurfaceGeometry<DOMRect> | null {
  const surface =
    clicked.closest<HTMLElement>(CBUTTON_SURFACE_SELECTOR) ??
    clicked.querySelector<HTMLElement>(CBUTTON_SURFACE_SELECTOR);
  if (surface == null) return null;
  const radius = Number.parseFloat(
    window.getComputedStyle(surface).borderTopLeftRadius,
  );
  if (!Number.isFinite(radius)) return null;
  return { rect: surface.getBoundingClientRect(), radius };
}

/** Builds the morph flight's source from the clicked card's VISUAL surface (not
 *  the outer hit wrapper) — see {@link resolveHeroCardMorphGeometry}. */
function buildHeroCardMorphSource(input: {
  cardContainerPreset?: ContainerPreset;
  team: TeamData;
  event?: ReactMouseEvent;
}): MorphSource | null {
  const element =
    input.event?.currentTarget instanceof HTMLElement
      ? input.event.currentTarget
      : null;
  if (element == null) return null;
  const { rect, radius } = resolveHeroCardMorphGeometry({
    surface: readHeroCardSurfaceGeometry(element),
    wrapperRect: element.getBoundingClientRect(),
  });
  return {
    rect,
    radius,
    cardVariant: null,
    heroCard: {
      teamData: input.team,
      cardContainerPreset: input.cardContainerPreset,
      cardBackground: HERO_TEAM_CARD_BACKGROUND,
      contentBackdropBlur: HERO_TEAM_CARD_CONTENT_BACKDROP_BLUR,
    },
  };
}

export function useCarouselCardClickHandler({
  cardContainerPreset,
  doubledIndex,
  isCentered,
  onScrollToIndex,
  onSelectTeam,
  team,
}: UseCarouselCardClickHandlerParams): (event?: ReactMouseEvent) => void {
  const clickInvestigationLog = useSeqLog(CAROUSEL_CARD_CLICK_INV_COMPONENT, {
    investigation: CLICK_INVESTIGATION_ID,
    teamId: team.id,
    doubledIndex,
  });

  return useCallback((event?: ReactMouseEvent): void => {
    clickInvestigationLog(
      CAROUSEL_CARD_HANDLE_CLICK_EVENT,
      {
        isCentered,
        teamName: team.name,
        branch: isCentered
          ? CarouselCardClickBranch.Select
          : CarouselCardClickBranch.ScrollToIndex,
      },
      LogLevel.Info,
    );

    if (isCentered) {
      onSelectTeam({
        team,
        morphSource: buildHeroCardMorphSource({
          cardContainerPreset,
          team,
          event,
        }),
      });
      return;
    }

    onScrollToIndex(doubledIndex);
  }, [
    cardContainerPreset,
    clickInvestigationLog,
    doubledIndex,
    isCentered,
    onScrollToIndex,
    onSelectTeam,
    team,
  ]);
}
